import { Box, Button, H4 } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord, mapRecordToArray } from '@deity-io/di-data-basics';
import Skeleton from '@material-ui/lab/Skeleton';
import { BaseHelmet, TRow, TableView, Thead } from 'components';
import { CACHE_FIRST } from 'contracts/constants';
import type { FC } from 'react';
import { useEffect } from 'react';
import {
  useExecuteButtonCommandMutation,
  useGetFlowLazyQuery,
  useGetPackageLazyQuery
} from 'services/graphql/generated/hooks';
import { v4, validate as validateUuid } from 'uuid';
import type { IRuntimeState } from '../IRuntimeState';
import { isIProducerInstance } from '../IRuntimeState';
import { isIButtonProducer } from './PackagePopoutFlow';
import { PackageVersionBadge } from './PackageVersionBadge';

interface IBtnProducersRowProps {
  isEditable?: boolean;
  itemKey: string;
  item: {
    accountUuid: string;
    producerKey: string;
    pipelineKey: string;
    packageVersionUuid: string;
    packageUuid: string;
    count: number;
    initialized: number;
    type: string;
    since: number;
    instances: IPropertyBag;
  };
}

const BtnProducersRow: FC<IBtnProducersRowProps> = ({ item, itemKey, isEditable = true }) => {
  const [getPackage, { loading: packageLoading, data: packageData }] = useGetPackageLazyQuery(CACHE_FIRST);

  const [getFlow, { loading: flowLoading, data: flowData }] = useGetFlowLazyQuery(CACHE_FIRST);
  useEffect(() => {
    if (item.packageUuid && validateUuid(item.packageUuid)) {
      getPackage({
        variables: {
          uuid: item.packageUuid
        }
      });
    }
    if (item.packageVersionUuid && validateUuid(item.packageVersionUuid)) {
      getFlow({
        variables: {
          packageVersionUuid: item.packageVersionUuid,
          uuid: item.pipelineKey.replaceAll('_', '-').substring(2)
        }
      });
    }
  }, [item]);

  const [executeCommand, { data: executeCommandResult, loading: executingCommand }] = useExecuteButtonCommandMutation();

  return (
    <>
      <H4 fontSize="lg" color="primaryText">
        ...-{item.accountUuid.split('-').pop()}
      </H4>

      <H4 fontSize="lg" color="primaryText">
        {packageData?.getPackage.name || item.packageUuid}
        <PackageVersionBadge packageVersionKey={item.packageVersionUuid} />
      </H4>

      <H4 fontSize="lg" color="primaryText" css={{ width: '100%' }}>
        {flowData?.getFlow.name || item.pipelineKey}
      </H4>
      <H4 fontSize="lg" color="primaryText">
        {item.initialized}/{item.initialized}
      </H4>
      <H4 fontSize="lg" color="primaryText">
        {item.count}
      </H4>
      <Button
        disabled={executingCommand}
        onClick={() => {
          if (flowData?.getFlow.uuid) {
            executeCommand({
              variables: {
                uuid: v4(),
                accountUuid: item.accountUuid,
                packageVersionUuid: item.packageVersionUuid,
                flowUuid: flowData.getFlow.uuid,
                componentKey: item.producerKey
              }
            });
          }

          return true;
        }}
      >
        Execute
      </Button>
    </>
  );
};

interface IStatusTabProps {
  isEditable?: boolean;
  loading?: boolean;
  loaded?: boolean;
  clusterState: IRuntimeState;
}

const BtnProducersTab: FC<IStatusTabProps> = ({ isEditable = true, clusterState, loading, loaded }) => {
  const producers: IPropertyBag = {};
  let producerCount = 0;
  // Group all button components on [packageVersion,pipeline]
  mapRecordToArray(clusterState.packages, (pack, packageKey) => {
    mapRecordToArray(pack.versions, (version, versionKey) => {
      mapRecordToArray(version.pipelines, (pipeline, pipelineKey) => {
        mapRecordToArray(pipeline.instances, (instance, instanceKey) => {
          if (isIProducerInstance(instance.data)) {
            mapRecord(instance.data.producers, producer => {
              if (isIButtonProducer(producer)) {
                const lookupKey = `${versionKey}_${pipelineKey}_${producer.key}`;
                if (!(lookupKey in producers)) {
                  producers[lookupKey] = {
                    accountUuid: pack.accountUuid,
                    producerKey: producer.key,
                    pipelineKey,
                    packageVersionUuid: versionKey,
                    packageUuid: packageKey,
                    count: 0,
                    initialized: 0,
                    type: producer.type,
                    since: Date.now(),
                    instances: {}
                  };
                }
                const localGroup = producers[lookupKey];
                localGroup.count += producer.count;
                localGroup.initialized += 1;
                localGroup.instances[instanceKey] = producer;
                if (localGroup.since > producer.since) {
                  localGroup.since = producer.since;
                }
                producerCount += 1;
              }
            });
          }
        });
      });
    });
  });

  return (
    <>
      <BaseHelmet page="Packages" />
      <Box>
        <Box
          style={{ border: '1px solid #E0E2E5' }}
          borderRadius="medium"
          pt="lg"
          pb="lg"
          bg="white"
          boxShadow="subtle"
        >
          <TableView gridTemplateColumns="1fr 1fr 1fr 0.4fr 0.4fr 0.4fr">
            <Thead
              headerItems={[
                { text: 'account' },
                { text: 'package' },
                { text: 'flow' },
                { text: 'instances' },
                { text: 'Msg count' },
                { text: 'actions' }
              ]}
            />

            {mapRecordToArray(producers, (item, itemKey) => (
              <TRow key={itemKey} rowMaxHeight="550px">
                <BtnProducersRow key={itemKey} item={item} itemKey={itemKey} />
              </TRow>
            ))}
            {loading && (
              <TRow>
                <>
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                  <Skeleton width="100%" height="40px" />
                </>
              </TRow>
            )}
          </TableView>
        </Box>
      </Box>
    </>
  );
};

export default BtnProducersTab;
